'use client'

import * as React from 'react'
import { Button } from '@/components/ui/button'
import { X, Copy } from 'lucide-react'

// Right sidebar context
type RightSidebarContextProps = {
    state: 'expanded' | 'collapsed'
    open: boolean
    setOpen: (open: boolean) => void
    toggleSidebar: () => void
}

const RightSidebarContext = React.createContext<RightSidebarContextProps | null>(null)

export function useRightSidebar() {
    const context = React.useContext(RightSidebarContext)
    if (!context) {
        throw new Error('useRightSidebar must be used within a RightSidebarProvider')
    }
    return context
}

export function RightSidebarProvider({
    children,
    defaultOpen = false,
}: {
    children: React.ReactNode
    defaultOpen?: boolean
}) {
    const [open, setOpen] = React.useState(defaultOpen)

    const toggleSidebar = React.useCallback(() => {
        setOpen((prev) => !prev)
    }, [])

    const state = open ? 'expanded' : 'collapsed'

    const contextValue = React.useMemo<RightSidebarContextProps>(
        () => ({
            state,
            open,
            setOpen,
            toggleSidebar,
        }),
        [state, open, setOpen, toggleSidebar]
    )

    return (
        <RightSidebarContext.Provider value={contextValue}>{children}</RightSidebarContext.Provider>
    )
}

// Right panel trigger component that works with right sidebar
export function RightPanelTrigger({ onClick, ...props }: React.ComponentProps<typeof Button>) {
    const { toggleSidebar } = useRightSidebar()

    return (
        <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
                onClick?.(e)
                toggleSidebar()
            }}
            {...props}
        />
    )
}

export function RightSidebar({
    children,
    content,
    isMobile = false,
    // whether to show the copy button next to the close (X) button
    showCopyButton = false,
    title,
}: {
    children?: React.ReactNode
    content?: React.ReactNode

    isMobile?: boolean
    showCopyButton?: boolean
    title?: string
}) {
    const { state } = useRightSidebar()

    const contentRef = React.useRef<HTMLDivElement | null>(null)
    const [isCopied, setIsCopied] = React.useState(false)

    const copySidebarContent = async () => {
        try {
            const contentText = contentRef.current?.innerText ?? ''
            if (!contentText) return
            await navigator.clipboard.writeText(contentText)
            setIsCopied(true)
            setTimeout(() => setIsCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy sidebar content', err)
        }
    }

    return (
        <div
            className="group peer text-sidebar-foreground h-full"
            data-state={state}
            data-collapsible={state === 'collapsed' ? 'offcanvas' : ''}
            data-variant="sidebar"
            data-side="right"
            data-slot="sidebar"
        >
            <div
                data-sidebar="sidebar"
                data-slot="sidebar-inner"
                className="bg-sidebar flex h-full w-full flex-col"
            >
                <div className="flex h-full w-full flex-col">
                    {/* Sticky Header */}
                    <div className="sticky top-0 z-50 backdrop-blur-sm bg-white dark:bg-black border-b border-sidebar-border/50">
                        <div className="flex items-center justify-between pl-5 pr-2.5 h-12.5">
                            {/* Title on the left */}
                            {title && (
                                <h2 className="text-sidebar-foreground font-semibold text-base">
                                    {title}
                                </h2>
                            )}

                            {/* Buttons on the right */}
                            <div className="flex items-center gap-2 ml-auto">
                                {showCopyButton && (
                                    <Button
                                        size="icon"
                                        className="bg-black/3 hover:bg-black/5 text-black/40 flex w-fit text-xs rounded-[30px] px-3 py-1.5 h-auto"
                                        onClick={copySidebarContent}
                                        aria-label={isCopied ? 'Copied' : 'Copy sidebar'}
                                    >
                                        <Copy size={14} /> {isCopied ? 'Copied' : 'Copy'}
                                    </Button>
                                )}

                                <RightSidebarTrigger />
                            </div>
                        </div>
                    </div>

                    <div
                        className={`flex-1 scrollbar-thin overflow-y-auto relative ${isMobile ? 'h-[calc(100vh)]' : 'h-[calc(100vh-3.125rem)]'}`}
                    >
                        <div
                            ref={contentRef}
                            data-sidebar-content
                            className="min-h-[100px] h-full flex flex-col"
                        >
                            {children || content || (
                                <p className="text-sidebar-foreground/70 text-sm mb-4">
                                    Right panel content will be implemented here.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// Internal trigger for the close button inside the right sidebar
function RightSidebarTrigger() {
    const { toggleSidebar } = useRightSidebar()

    return (
        <Button
            size="icon"
            className="h-7 w-7 text-black/40 bg-black/3 hover:bg-black/5 rounded-[30px]"
            onClick={toggleSidebar}
        >
            <X />
        </Button>
    )
}
